import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, CheckCircle, UserPlus, AlertTriangle, MessageSquare, FileText, Zap, User } from 'lucide-react';
import { HubTask, escalateTask, sendClientMessage } from '../../../api/onboardingClient';

interface TaskDrawerProps {
  task: HubTask | null;
  isOpen: boolean;
  onClose: () => void;
  onMarkDone: (taskId: string) => void;
  onReassign: (taskId: string, userId: string) => void;
  onEscalate: (taskId: string) => void;
  onAddNote: (taskId: string, note: string) => void;
}

export const TaskDrawer: React.FC<TaskDrawerProps> = ({
  task,
  isOpen,
  onClose,
  onMarkDone,
  onReassign,
  onEscalate,
  onAddNote
}) => {
  const [note, setNote] = useState('');
  const [isEscalating, setIsEscalating] = useState(false);

  const handleEscalate = async () => {
    if (!task) return;
    
    setIsEscalating(true);
    try {
      if (task.projectId) {
        await escalateTask(task.projectId, task.id);
      }
      onEscalate(task.id);
    } catch (error) {
      console.error('Failed to escalate task:', error);
    } finally {
      setIsEscalating(false);
    }
  };

  const handleAddNote = async () => {
    if (!task || !note.trim()) return;
    
    try {
      if (task.projectId) {
        await sendClientMessage(task.projectId, { text: `Note on ${task.title}: ${note}` });
      }
      onAddNote(task.id, note);
      setNote('');
    } catch (error) {
      console.error('Failed to add note:', error);
    }
  };

  if (!task) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-40"
            onClick={onClose}
          />
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed right-0 top-0 h-full w-96 bg-[#4267A1] border-l border-[#4267A1]/30 shadow-2xl z-50 overflow-y-auto"
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold bg-gradient-to-r from-white to-slate-300 bg-clip-text text-transparent">Task Details</h2>
                <button onClick={onClose} className="p-2 text-slate-400 hover:text-white hover:bg-[#4267A1]/20 rounded-lg transition-all duration-200">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-6">
                <div>
                  <h3 className="font-semibold text-white mb-4 text-lg">{task.title}</h3>
                  <div className="space-y-6">
                    <div className="tyler-card p-4">
                      <label className="text-sm text-slate-400 mb-2 block">Phase</label>
                      <p className="text-white font-semibold text-lg">{task.phase}</p>
                    </div>
                    <div className="tyler-card p-4">
                      <label className="text-sm text-slate-400 mb-2 block">Due Date</label>
                      <p className="text-white font-semibold text-lg">{new Date(task.due).toLocaleDateString()}</p>
                    </div>
                    <div className="tyler-card p-4">
                      <label className="text-sm text-slate-400 mb-2 block">Estimated Hours</label>
                      <p className="text-white font-semibold text-lg">{task.estHours}h</p>
                    </div>
                    <div className="tyler-card p-4">
                      <label className="text-sm text-slate-400 mb-2 block">Status</label>
                      <span className={`px-3 py-1.5 text-sm font-medium rounded-full ${
                        task.status === 'done' ? 'tyler-status-completed' :
                        task.status === 'doing' ? 'tyler-status-active' :
                        task.status === 'blocked' ? 'tyler-status-overdue' :
                        'tyler-status-pending'
                      }`}>
                        {task.status.charAt(0).toUpperCase() + task.status.slice(1).replace('-', ' ')}
                      </span>
                    </div>
                  </div>
                </div>

                {task.automations.length > 0 && (
                  <div className="mt-8">
                    <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                      <Zap className="w-5 h-5 mr-2 text-[#5A7BC4]" />
                      Active Automations
                    </h3>
                    <div className="flex flex-wrap gap-3">
                      {task.automations.map((automation, index) => (
                        <span
                          key={index}
                          className="px-3 py-2 text-sm bg-gradient-to-r from-[#4267A1]/20 to-[#5A7BC4]/20 text-[#5A7BC4] rounded-lg border border-[#4267A1]/30 font-medium"
                        >
                          {automation}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div className="mt-8 space-y-4">
                  <button
                    onClick={() => onMarkDone(task.id)}
                    disabled={task.status === 'done'}
                    className={`w-full px-6 py-3 rounded-xl font-semibold transition-all duration-200 flex items-center justify-center ${
                      task.status === 'done'
                        ? 'bg-white border border-gray-300 text-gray-400 cursor-not-allowed'
                        : 'bg-gradient-to-r from-[#8A9150] to-[#A5B366] text-white hover:shadow-lg hover:shadow-[#8A9150]/25'
                    }`}
                  >
                    <CheckCircle className="w-5 h-5 mr-2" />
                    {task.status === 'done' ? 'Completed' : 'Mark Done'}
                  </button>

                  <button
                    onClick={() => onReassign(task.id, 'new-user')}
                    className="w-full px-6 py-3 bg-gradient-to-r from-[#4267A1] to-[#5A7BC4] text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-[#4267A1]/25 transition-all duration-200 flex items-center justify-center"
                  >
                    <User className="w-5 h-5 mr-2" />
                    Reassign
                  </button>

                  <button
                    onClick={handleEscalate}
                    disabled={isEscalating}
                    className="w-full px-6 py-3 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-red-600/25 transition-all duration-200 flex items-center justify-center disabled:opacity-50"
                  >
                    <AlertTriangle className="w-5 h-5 mr-2" />
                    {isEscalating ? 'Escalating...' : 'Escalate'}
                  </button>

                  {task.supportTicket && (
                    <div className="p-4 tyler-card border border-[#4267A1]/30">
                      <p className="text-sm font-medium text-[#5A7BC4] mb-2">Support Ticket</p>
                      <a href={task.supportTicket.url} className="text-sm text-[#5A7BC4] hover:text-white transition-colors">
                        {task.supportTicket.id} - {task.supportTicket.status}
                      </a>
                    </div>
                  )}
                </div>

                <div className="mt-8">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                    <MessageSquare className="w-5 h-5 mr-2 text-[#5A7BC4]" />
                    Add Note
                  </h3>
                  <textarea
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Add a note about this task..."
                    className="w-full h-28 px-4 py-3 tyler-card border border-[#4267A1]/30 rounded-xl text-white placeholder-slate-400 resize-none focus:outline-none focus:border-[#4267A1] focus:shadow-lg focus:shadow-[#4267A1]/25 transition-all duration-200"
                  />
                  <button
                    onClick={handleAddNote}
                    disabled={!note.trim()}
                    className="mt-4 px-6 py-3 bg-gradient-to-r from-[#8A9150] to-[#A5B366] text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-[#8A9150]/25 transition-all duration-200 disabled:opacity-50"
                  >
                    Add Note
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};
